import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/feature/ProductCard';
import {
  KASPREADSHEET_PRODUCTS,
  PRODUCT_CATEGORIES,
} from '@/mocks/kaspreadsheet-products';

export default function ProductsPage() {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const currentLang = location.pathname.split('/')[1] || 'en';

  useEffect(() => {
    const lang = location.pathname.split('/')[1];
    if (lang && ['en', 'pt', 'de'].includes(lang)) {
      i18n.changeLanguage(lang);
    } else {
      i18n.changeLanguage('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, i18n]);

  const filteredProducts =
    activeCategory === 'All'
      ? KASPREADSHEET_PRODUCTS
      : KASPREADSHEET_PRODUCTS.filter((p) => p.category === activeCategory);

  const productCount = filteredProducts.length;

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary-600 to-primary-500 text-background-50 py-12 md:py-16">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20fashion%20product%20grid%20background%20dark%20minimal%20geometric%20shapes%20soft%20light%20elegant%20texture&width=1600&height=600&seq=products-hero-2026&orientation=landscape')] bg-cover bg-center" />
          </div>
          <div className="relative w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block text-sm font-semibold uppercase tracking-wider text-primary-200 mb-3">
                {t('products.heroTagline')}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading mb-4">
                {t('products.heroTitle')}
              </h1>
              <p className="text-base md:text-lg text-primary-100 max-w-2xl mx-auto mb-6">
                {t('products.heroSubtitle')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-primary-600 font-semibold rounded-lg hover:bg-background-100 transition-colors"
              >
                {t('cta.buyNow')}
                <i className="ri-external-link-line" />
              </a>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200">
          <div className="w-full px-4 md:px-8 lg:px-12 py-3">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
              <button
                onClick={() => setActiveCategory('All')}
                className={`px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${
                  activeCategory === 'All'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {t('products.allCategories')}
                <span className="ml-1.5 text-xs opacity-70">
                  ({KASPREADSHEET_PRODUCTS.length})
                </span>
              </button>
              {PRODUCT_CATEGORIES.map((cat) => {
                const count = KASPREADSHEET_PRODUCTS.filter(
                  (p) => p.category === cat
                ).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${
                      activeCategory === cat
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    {cat}
                    <span className="ml-1.5 text-xs opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              {activeCategory === 'All'
                ? t('products.allTitle')
                : activeCategory}
            </h2>
            <span className="text-sm text-foreground-500">
              {productCount} {t('products.items')}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <i className="ri-shopping-bag-line text-4xl text-foreground-300 mb-3" />
              <p className="text-foreground-500">{t('products.noResults')}</p>
            </div>
          )}
        </section>

        {/* CTA Banner */}
        <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl p-6 md:p-10 text-center text-background-50">
            <h2 className="text-xl md:text-2xl font-bold font-heading mb-3">
              {t('products.ctaTitle')}
            </h2>
            <p className="text-accent-100 text-sm md:text-base max-w-xl mx-auto mb-5">
              {t('products.ctaSubtitle')}
            </p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-accent-600 font-semibold rounded-lg hover:bg-background-100 transition-colors"
            >
              {t('cta.buyNow')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}